import { useState, useEffect } from "react";
import { T, S, fmtDate } from "./theme";
import { Badge, Btn, Drawer, DrawerMeta, DrawerSection, DrawerField } from "./components";
import { fetchPylonIssues, fetchPylonIssue } from "./api";

const STATE_LABELS = {
  new: "New",
  waiting_on_you: "Waiting on You",
  waiting_on_customer: "Waiting on Customer",
  on_hold: "On Hold",
  closed: "Closed",
};
const STATE_COLORS = {
  new: "orange",
  waiting_on_you: "red",
  waiting_on_customer: "blue",
  on_hold: "orange",
  closed: "green",
};

const TAG_COLORS = [
  { bg: "rgba(91,75,213,0.10)", color: "#5B4BD5" },
  { bg: "rgba(10,160,110,0.10)", color: "#0AA06E" },
  { bg: "rgba(43,127,212,0.10)", color: "#2B7FD4" },
  { bg: "rgba(212,138,0,0.10)", color: "#D48A00" },
  { bg: "rgba(220,69,69,0.10)", color: "#DC4545" },
  { bg: "rgba(156,80,180,0.10)", color: "#9C50B4" },
  { bg: "rgba(0,150,136,0.10)", color: "#009688" },
  { bg: "rgba(233,100,67,0.10)", color: "#E96443" },
];
const tagColor = (tag) => {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) hash = tag.charCodeAt(i) + ((hash << 5) - hash);
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length];
};

export default function HelpDesk({ showToast }) {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [drawer, setDrawer] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadIssues();
  }, []);

  const loadIssues = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchPylonIssues({ limit: 200 });
      setIssues(res.data || []);
    } catch (err) {
      console.error("Failed to load Pylon issues:", err);
      setError("Failed to load tickets from Pylon. Check that the API key is configured.");
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (issue) => {
    setDrawer({ issue });
    setDrawerLoading(true);
    try {
      const res = await fetchPylonIssue(issue.id);
      setDrawer({ issue: res.data || issue });
    } catch {
      // keep the summary-level data we already have
    } finally {
      setDrawerLoading(false);
    }
  };

  const allStates = [...new Set(issues.map((i) => i.state))].sort();
  const allSources = [...new Set(issues.map((i) => i.source).filter(Boolean))].sort();
  const allTags = [...new Set(issues.flatMap((i) => i.tags || []))].sort();
  const allAssignees = [...new Set(issues.map((i) => i.assignee).filter(Boolean))].sort();

  const filtered = issues.filter((i) => {
    if (filter === "open" && i.state === "closed") return false;
    if (filter === "closed" && i.state !== "closed") return false;
    if (stateFilter && i.state !== stateFilter) return false;
    if (sourceFilter && i.source !== sourceFilter) return false;
    if (tagFilter && !(i.tags || []).includes(tagFilter)) return false;
    if (assigneeFilter && i.assignee !== assigneeFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !(i.title || "").toLowerCase().includes(q) &&
        !(i.requester || "").toLowerCase().includes(q) &&
        !(i.requester_email || "").toLowerCase().includes(q) &&
        !(i.tags || []).some((t) => t.toLowerCase().includes(q)) &&
        !String(i.number).includes(q)
      )
        return false;
    }
    return true;
  });

  const openCount = issues.filter((i) => i.state !== "closed").length;
  const closedCount = issues.filter((i) => i.state === "closed").length;

  const perPage = 25;
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: T.textDim }}>Loading tickets from Pylon...</div>;
  }

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>⚠️</div>
        <div style={{ fontSize: 14, color: T.red, marginBottom: 16 }}>{error}</div>
        <Btn variant="primary" onClick={loadIssues}>Retry</Btn>
      </div>
    );
  }

  return (
    <>
      {/* Integration status banner */}
      <div style={{ background: T.greenSoft, border: "1px solid rgba(10,160,110,0.15)", borderRadius: T.radius, padding: "14px 20px", marginBottom: 24, display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontSize: 16 }}>✓</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.green }}>Pylon Integration — Connected</div>
          <div style={{ fontSize: 12, color: T.textMuted }}>Showing {issues.length} issues from Pylon API. Data refreshes on page load.</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <Btn sm variant="ghost" onClick={loadIssues}>Refresh</Btn>
        </div>
      </div>

      {/* Summary tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 24 }}>
        <div style={{ background: T.surface, border: "none", borderRadius: T.radius, padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ fontSize: 11.5, color: T.textDim, fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Open Issues</div>
          <div style={{ fontSize: 28, fontWeight: 700, fontFamily: T.mono, color: T.orange }}>{openCount}</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>Live from Pylon</div>
        </div>
        <div style={{ background: T.surface, border: "none", borderRadius: T.radius, padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ fontSize: 11.5, color: T.textDim, fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Closed Issues</div>
          <div style={{ fontSize: 28, fontWeight: 700, fontFamily: T.mono, color: T.green }}>{closedCount}</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>Live from Pylon</div>
        </div>
        <div style={{ background: T.surface, border: "none", borderRadius: T.radius, padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ fontSize: 11.5, color: T.textDim, fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Avg First Response</div>
          <div style={{ fontSize: 28, fontWeight: 700, fontFamily: T.mono, color: T.blue }}>
            {(() => {
              const withFr = issues.filter((i) => i.first_response_seconds != null);
              if (withFr.length === 0) return "—";
              const avg = Math.round(withFr.reduce((s, i) => s + i.first_response_seconds, 0) / withFr.length);
              if (avg < 60) return `${avg}s`;
              if (avg < 3600) return `${Math.floor(avg / 60)}m`;
              return `${Math.floor(avg / 3600)}h ${Math.floor((avg % 3600) / 60)}m`;
            })()}
          </div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>Across loaded issues</div>
        </div>
        <div style={{ background: T.surface, border: "none", borderRadius: T.radius, padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ fontSize: 11.5, color: T.textDim, fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Avg Resolution Time</div>
          <div style={{ fontSize: 28, fontWeight: 700, fontFamily: T.mono, color: T.accent }}>
            {(() => {
              const withRes = issues.filter((i) => i.resolution_seconds != null);
              if (withRes.length === 0) return "—";
              const avg = Math.round(withRes.reduce((s, i) => s + i.resolution_seconds, 0) / withRes.length);
              if (avg < 60) return `${avg}s`;
              if (avg < 3600) return `${Math.floor(avg / 60)}m`;
              return `${Math.floor(avg / 3600)}h ${Math.floor((avg % 3600) / 60)}m`;
            })()}
          </div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>Across resolved issues</div>
        </div>
      </div>

      {/* By State breakdown */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 16, marginBottom: 24 }}>
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 16 }}>By Status</div>
          {Object.entries(
            issues.reduce((acc, i) => {
              const s = i.state || "unknown";
              acc[s] = (acc[s] || 0) + 1;
              return acc;
            }, {})
          )
            .sort(([, a], [, b]) => b - a)
            .map(([state, count]) => {
              const knownColor = STATE_COLORS[state];
              const tc = !knownColor ? tagColor(state) : null;
              return (
                <div key={state} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${T.border}` }}>
                  {knownColor
                    ? <Badge variant={knownColor}>{STATE_LABELS[state] || state}</Badge>
                    : <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 4, fontSize: 11, fontWeight: 500, background: tc.bg, color: tc.color }}>{state}</span>
                  }
                  <span style={{ fontFamily: T.mono, fontSize: 14, fontWeight: 600 }}>{count}</span>
                </div>
              );
            })}
        </div>
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 16 }}>By Tag</div>
          {(() => {
            const tagCounts = {};
            issues.forEach((i) => (i.tags || []).forEach((t) => (tagCounts[t] = (tagCounts[t] || 0) + 1)));
            const sorted = Object.entries(tagCounts).sort(([, a], [, b]) => b - a).slice(0, 8);
            const max = sorted.length > 0 ? sorted[0][1] : 1;
            if (sorted.length === 0)
              return <div style={{ fontSize: 12, color: T.textDim, padding: "20px 0", textAlign: "center" }}>No tags found on loaded issues</div>;
            return sorted.map(([tag, count]) => (
              <div key={tag} style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                  <span style={{ color: T.text }}>{tag}</span>
                  <span style={{ color: T.textMuted }}>{count}</span>
                </div>
                <div style={{ background: T.surface2, borderRadius: 4, height: 6 }}>
                  <div style={{ background: T.accent, height: 6, borderRadius: 4, width: `${(count / max) * 100}%`, transition: "width 0.5s" }} />
                </div>
              </div>
            ));
          })()}
        </div>
      </div>

      {/* Filters + table */}
      <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${T.border}`, marginBottom: 20 }}>
        {[{ key: "all", label: `All (${issues.length})` }, { key: "open", label: `Open (${openCount})` }, { key: "closed", label: `Closed (${closedCount})` }].map(({ key, label }) => (
          <button key={key} style={S.innertab(filter === key)} onClick={() => { setFilter(key); setPage(1); }}>{label}</button>
        ))}
      </div>
      {(() => {
        const pillDrop = {
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "7px 14px", borderRadius: 20,
          border: `1px solid ${T.border}`,
          background: T.surface, color: T.text,
          fontSize: 13, fontWeight: 500, fontFamily: T.font,
          cursor: "pointer", position: "relative",
        };
        const hiddenSelect = {
          position: "absolute", inset: 0, opacity: 0, cursor: "pointer",
          width: "100%", height: "100%", fontSize: 13,
        };
        const chevron = <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={T.textDim} strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg>;
        const selPill = {
          display: "inline-flex", alignItems: "center", gap: 4,
          padding: "3px 10px", borderRadius: 12,
          background: T.accentSoft, color: T.accent,
          fontSize: 11, fontWeight: 500,
        };
        const xBtn = { background: "none", border: "none", color: T.accent, cursor: "pointer", fontSize: 11, lineHeight: 1, padding: "0 0 0 2px", fontWeight: 600 };
        const hasFilters = tagFilter || stateFilter || assigneeFilter || sourceFilter;
        return (
          <>
            <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
              <div style={pillDrop}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                <span>Tags</span>
                {chevron}
                <select style={hiddenSelect} value="" onChange={(e) => { if (e.target.value) { setTagFilter(e.target.value); setPage(1); } }}>
                  <option value="">Select tag...</option>
                  {allTags.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={pillDrop}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                <span>Status</span>
                {chevron}
                <select style={hiddenSelect} value="" onChange={(e) => { if (e.target.value) { setStateFilter(e.target.value); setPage(1); } }}>
                  <option value="">Select status...</option>
                  {allStates.map((s) => <option key={s} value={s}>{STATE_LABELS[s] || s}</option>)}
                </select>
              </div>
              <div style={pillDrop}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                <span>Assignee</span>
                {chevron}
                <select style={hiddenSelect} value="" onChange={(e) => { if (e.target.value) { setAssigneeFilter(e.target.value); setPage(1); } }}>
                  <option value="">Select assignee...</option>
                  {allAssignees.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div style={pillDrop}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                <span>Source</span>
                {chevron}
                <select style={hiddenSelect} value="" onChange={(e) => { if (e.target.value) { setSourceFilter(e.target.value); setPage(1); } }}>
                  <option value="">Select source...</option>
                  {allSources.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            {hasFilters && (
              <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
                {tagFilter && (
                  <span style={selPill}>{tagFilter} <button style={xBtn} onClick={() => { setTagFilter(""); setPage(1); }}>✕</button></span>
                )}
                {stateFilter && (
                  <span style={selPill}>{STATE_LABELS[stateFilter] || stateFilter} <button style={xBtn} onClick={() => { setStateFilter(""); setPage(1); }}>✕</button></span>
                )}
                {assigneeFilter && (
                  <span style={selPill}>{assigneeFilter} <button style={xBtn} onClick={() => { setAssigneeFilter(""); setPage(1); }}>✕</button></span>
                )}
                {sourceFilter && (
                  <span style={selPill}>{sourceFilter} <button style={xBtn} onClick={() => { setSourceFilter(""); setPage(1); }}>✕</button></span>
                )}
                <button style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 11, fontFamily: T.font }} onClick={() => { setTagFilter(""); setStateFilter(""); setAssigneeFilter(""); setSourceFilter(""); setPage(1); }}>Clear all</button>
              </div>
            )}
          </>
        );
      })()}
      <div style={{ marginBottom: 16 }}>
        <input style={S.filterInput} placeholder="Search issues..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
      </div>
      <div style={S.tableWrap}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>{["#", "Title", "Status", "Tags", "Requester", "1st Response", "Resolution", "Created"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {paged.map((t) => (
              <tr key={t.id} style={{ cursor: "pointer", transition: "background 0.1s" }} onMouseEnter={(e) => (e.currentTarget.style.background = T.accentSoft)} onMouseLeave={(e) => (e.currentTarget.style.background = "")} onClick={() => openDetail(t)}>
                <td style={{ ...S.td, ...S.mono }}>{t.number}</td>
                <td style={{ ...S.td, maxWidth: 280 }}>
                  <div style={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.title}</div>
                </td>
                <td style={S.td}>{STATE_COLORS[t.state] ? <Badge variant={STATE_COLORS[t.state]}>{STATE_LABELS[t.state] || t.state}</Badge> : (() => { const tc = tagColor(t.state); return <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 4, fontSize: 11, fontWeight: 500, background: tc.bg, color: tc.color }}>{t.state}</span>; })()}</td>
                <td style={{ ...S.td, maxWidth: 200 }}>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {(t.tags || []).slice(0, 3).map((tag) => {
                      const tc = tagColor(tag);
                      return <span key={tag} style={{ display: "inline-block", padding: "2px 7px", borderRadius: 4, fontSize: 10, fontWeight: 500, background: tc.bg, color: tc.color }}>{tag}</span>;
                    })}
                    {(t.tags || []).length > 3 && <span style={{ fontSize: 10, color: T.textDim }}>+{t.tags.length - 3}</span>}
                  </div>
                </td>
                <td style={{ ...S.td, fontSize: 12, color: T.textMuted }}>{t.requester || "—"}</td>
                <td style={{ ...S.td, fontSize: 12 }}>{t.first_response_display || "—"}</td>
                <td style={{ ...S.td, fontSize: 12 }}>{t.resolution_display || (t.state === "closed" ? "—" : "Pending")}</td>
                <td style={{ ...S.td, fontSize: 12, color: T.textMuted }}>{fmtDate(t.created_at)}</td>
              </tr>
            ))}
            {paged.length === 0 && (
              <tr><td colSpan={8} style={{ ...S.td, textAlign: "center", color: T.textDim, padding: 40 }}>No issues found</td></tr>
            )}
          </tbody>
        </table>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: `1px solid ${T.border}` }}>
          <span style={{ fontSize: 12, color: T.textMuted }}>
            Showing {filtered.length > 0 ? (currentPage - 1) * perPage + 1 : 0}–{Math.min(currentPage * perPage, filtered.length)} of {filtered.length} issues
          </span>
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <Btn sm variant="ghost" onClick={() => setPage((p) => Math.max(1, p - 1))} style={{ opacity: currentPage <= 1 ? 0.4 : 1 }}>Prev</Btn>
            <span style={{ fontSize: 12, color: T.textMuted, padding: "0 8px" }}>Page {currentPage} of {totalPages}</span>
            <Btn sm variant="ghost" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} style={{ opacity: currentPage >= totalPages ? 0.4 : 1 }}>Next</Btn>
          </div>
        </div>
      </div>

      {/* Planned features */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 20, marginTop: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Under Development</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {[
            { feature: "Tier Classification", detail: "Ticket tier categorization (Tier 1/2/3) based on Pylon custom fields or tags", status: "Needs custom field setup in Pylon" },
            { feature: "Site Correlation", detail: "Cross-reference ticket volume with station utilization data", status: "Needs site_id mapping on tickets" },
            { feature: "Webhook Receiver", detail: "Real-time ticket sync via Pylon webhooks for instant updates and local caching", status: "Planned for Phase 3" },
            { feature: "Historical Trend Charts", detail: "Ticket volume and resolution trends over time", status: "Needs historical data pipeline" },
          ].map(({ feature, detail, status }) => (
            <div key={feature} style={{ background: T.surface2, borderRadius: T.radiusSm, padding: "12px 16px" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.text, marginBottom: 4 }}>{feature}</div>
              <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>{detail}</div>
              <Badge variant="orange">{status}</Badge>
            </div>
          ))}
        </div>
      </div>

      {/* Ticket detail drawer */}
      <Drawer
        open={!!drawer}
        title={drawer?.issue ? `Issue #${drawer.issue.number}` : ""}
        onClose={() => setDrawer(null)}
        footer={
          <div style={{ display: "flex", gap: 8 }}>
            {drawer?.issue?.link && (
              <Btn variant="primary" onClick={() => window.open(drawer.issue.link, "_blank")}>Open in Pylon</Btn>
            )}
            <Btn variant="ghost" onClick={() => setDrawer(null)}>Close</Btn>
          </div>
        }
      >
        {drawer?.issue && (() => {
          const t = drawer.issue;
          const statusBadge = STATE_COLORS[t.state]
            ? <Badge variant={STATE_COLORS[t.state]}>{STATE_LABELS[t.state] || t.state}</Badge>
            : (() => { const tc = tagColor(t.state); return <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 4, fontSize: 11, fontWeight: 500, background: tc.bg, color: tc.color }}>{t.state}</span>; })();
          return (
            <>
              {/* Header */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>{fmtDate(t.created_at)}</div>
                <div style={{ fontSize: 17, fontWeight: 600, color: T.text, lineHeight: 1.4 }}>{t.title}</div>
              </div>

              <div style={{ height: 1, background: T.border, marginBottom: 20 }} />

              {/* Status & Source */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Status</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  {statusBadge}
                  {t.source && <span style={{ fontSize: 11, color: T.textMuted, background: T.surface2, padding: "3px 8px", borderRadius: 4 }}>{t.source}</span>}
                </div>
              </div>

              {/* Tags */}
              {(t.tags || []).length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Tags</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {t.tags.map((tag) => {
                      const tc = tagColor(tag);
                      return <span key={tag} style={{ display: "inline-block", padding: "4px 12px", borderRadius: 12, fontSize: 11, fontWeight: 500, background: tc.bg, color: tc.color }}>{tag}</span>;
                    })}
                  </div>
                </div>
              )}

              <div style={{ height: 1, background: T.border, marginBottom: 20 }} />

              {/* People */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: T.accent, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Requester</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 34, height: 34, borderRadius: "50%", background: T.accentSoft, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 600, color: T.accent }}>
                      {(t.requester || "?")[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{t.requester || "—"}</div>
                      {t.requester_email && t.requester_email !== t.requester && <div style={{ fontSize: 11, color: T.textMuted }}>{t.requester_email}</div>}
                    </div>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: T.green, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Assignee</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 34, height: 34, borderRadius: "50%", background: t.assignee ? T.greenSoft : T.surface3, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 600, color: t.assignee ? T.green : T.textDim }}>
                      {(t.assignee || "?")[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{t.assignee || "Unassigned"}</div>
                      {t.assignee_email && t.assignee_email !== t.assignee && <div style={{ fontSize: 11, color: T.textMuted }}>{t.assignee_email}</div>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              {t.body_html && (
                <>
                  <div style={{ height: 1, background: T.border, marginBottom: 20 }} />
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: T.blue, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Description</div>
                    <div
                      className="pylon-body"
                      style={{ fontSize: 13, lineHeight: 1.7, color: T.text, borderLeft: `2px solid ${T.blue}`, paddingLeft: 14, maxHeight: 240, overflowY: "auto", overflowX: "auto", wordBreak: "break-word" }}
                      ref={(el) => {
                        if (!el) return;
                        el.querySelectorAll("table").forEach((tbl) => {
                          tbl.style.width = "100%";
                          tbl.style.fontSize = "12px";
                          tbl.style.borderCollapse = "collapse";
                          tbl.style.tableLayout = "fixed";
                        });
                        el.querySelectorAll("th, td").forEach((cell) => {
                          cell.style.padding = "6px 10px";
                          cell.style.borderBottom = `1px solid ${T.border}`;
                          cell.style.wordBreak = "break-word";
                          cell.style.fontSize = "12px";
                          cell.style.fontWeight = cell.tagName === "TH" ? "600" : "400";
                        });
                      }}
                      dangerouslySetInnerHTML={{ __html: t.body_html }}
                    />
                  </div>
                </>
              )}

              <div style={{ height: 1, background: T.border, marginBottom: 20 }} />

              {/* Timing */}
              <div style={{ fontSize: 10, fontWeight: 600, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>Timing</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
                <div style={{ borderRadius: T.radiusSm, padding: "14px 12px", textAlign: "center", border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 6, fontWeight: 500 }}>Created</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{fmtDate(t.created_at)}</div>
                </div>
                <div style={{ borderRadius: T.radiusSm, padding: "14px 12px", textAlign: "center", background: t.first_response_display ? T.blueSoft : "transparent", border: `1px solid ${t.first_response_display ? "rgba(43,127,212,0.2)" : T.border}` }}>
                  <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 6, fontWeight: 500 }}>1st Response</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.first_response_display ? T.blue : T.textDim }}>{t.first_response_display || "—"}</div>
                </div>
                <div style={{ borderRadius: T.radiusSm, padding: "14px 12px", textAlign: "center", background: t.resolution_display ? T.greenSoft : "transparent", border: `1px solid ${t.resolution_display ? "rgba(10,160,110,0.2)" : T.border}` }}>
                  <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 6, fontWeight: 500 }}>Resolution</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.resolution_display ? T.green : T.textDim }}>{t.resolution_display || "Pending"}</div>
                </div>
              </div>

              {/* Under Development */}
              <div style={{ background: T.orangeSoft, borderRadius: T.radiusSm, padding: "12px 16px" }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: T.orange, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4 }}>Coming Soon</div>
                <div style={{ fontSize: 12, color: T.textMuted }}>Tier, category, and site correlation are under development.</div>
              </div>

              {drawerLoading && <div style={{ fontSize: 12, color: T.textDim, marginTop: 12 }}>Loading full details...</div>}
            </>
          );
        })()}
      </Drawer>
    </>
  );
}
